<script setup>
import Card from '@/components/Shared/Card.vue';
import { computed, inject, ref } from 'vue';
import { useForm } from '@inertiajs/vue3';
import FormEditor from '@/components/Shared/Form/Editor/Index.vue';

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  isCustomer: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit']);

const trans = inject('trans');
const editor = ref(null);

const form = useForm({
  status: props.ticket.statusValue?.id,
  message: '',
});

const submitAsOptions = [
  { value: 0, label: trans('__JSON__.Open') },
  { value: 5, label: trans('__JSON__.Pending') },
  { value: 6, label: trans('__JSON__.Done') },
];

const submitAsButtonText = computed(() => {
  if (props.isCustomer) {
    return trans('__JSON__.Submit');
  }
  return trans('__JSON__.Submit as') + ' ' + submitAsOptions.find(option => option.value === form.status)?.label;
});

const isUserDeleted = computed(() => {
  return props.ticket.user === null;
});

const replyRoute = computed(() => {
  return props.isCustomer
    ? route('dashboard.support.ticket.reply', props.ticket.id)
    : route('dashboard.partner.leads.reply', props.ticket.id);
});

const replyToName = computed(() => {
  return props.isCustomer
    ? props.ticket.sendTo.partner.companyName
    : props.ticket.user?.name || trans('__JSON__.User Deleted');
});

const submit = () => {
  if (props.isCustomer) {
    form.status = null;
  }

  form.post(replyRoute.value, {
    preserveScroll: true,
    onSuccess: () => {
      form.reset();
      editor.value.reset();
    },
  });
};
</script>

<template>
  <card>
    <template #header>
      <div class="d-flex justify-content-start gap">
        <div class="font-bold text-lg">
          {{ trans('__JSON__.Reply To') }}
          <span
            class="ml-2"
            :class="[!isCustomer && isUserDeleted ? 'color_danger font-bold' : 'text-muted font-medium']"
          >
            {{ replyToName }}
          </span>
        </div>
      </div>
    </template>
    <template #body>
      <div>
        <form-editor
          ref="editor"
          v-model="form.message"
          style="background: white"
          :placeholder="trans('__JSON__.Reply here')"
          :error="form.errors.message"
        ></form-editor>
      </div>
    </template>
    <template #footer>
      <div class="d-flex justify-content-end">
        <div v-if="isUserDeleted" class="d-flex items-center mr-2">
          <p class="color_danger">{{ trans('__JSON__.Reply is disabled because the user is not active') }}</p>
        </div>
        <button
          @click="submit"
          class="button"
          :class="{ button_suffix: !isCustomer, button_disabled: isUserDeleted }"
          :disabled="isUserDeleted"
        >
          {{ submitAsButtonText }}
        </button>
        <button
          v-if="!isCustomer"
          class="button button_dropdown"
          :class="{ button_disabled: isUserDeleted }"
          id="submit-dropdown"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
          :disabled="isUserDeleted"
        >
          <svg class="icon" style="width: 20px; height: 20px">
            <use xlink:href="#chevron"></use>
          </svg>
        </button>
        <div v-if="!isCustomer" class="dropdown-menu dropdown-menu-right" aria-labelledby="submit-dropdown">
          <button
            v-for="submit in submitAsOptions"
            :key="submit.value"
            type="button"
            class="dropdown-item"
            @click="form.status = submit.value"
          >
            {{ trans('__JSON__.Submit as') }} {{ submit.label }}
          </button>
        </div>
      </div>
    </template>
  </card>
</template>
